import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from '@/components/ui/command';
import { useHotkeys } from 'react-hotkeys-hook';
import { useCopilotChat, useCopilotAction } from '@copilotkit/react-core';
import { useConversation } from '@11labs/react';
import { useSparcAgent } from '@/hooks/sparc/useSparcAgent';
import { recordInteraction } from '@/services/agentic/sparcSessionService';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useVoiceCommands } from '@/hooks/useVoiceCommands';
import { 
  Search, 
  Mic, 
  MicOff, 
  Bot, 
  Brain, 
  MessageSquare, 
  Send, 
  X,
  Loader,
  VolumeX,
  Volume2
} from 'lucide-react';
import { AgentRole } from '@/components/agentic/agents/types';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const agentRoles: { id: AgentRole; name: string; icon: React.ReactNode }[] = [
  { id: 'mentor', name: 'Mentor', icon: <Brain className="h-4 w-4" /> },
  { id: 'student', name: 'Student', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'admin', name: 'Admin', icon: <Bot className="h-4 w-4" /> },
  { id: 'expert', name: 'Expert', icon: <Brain className="h-4 w-4" /> },
];

const taskCategories = [
  'learning',
  'coding',
  'research',
  'organization',
];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<AgentRole>('student');
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [audioResponse, setAudioResponse] = useState<string | null>(null);
  
  const { 
    initialized,
    config,
    updateConfig,
    execute
  } = useSparcAgent({
    autoInitialize: true
  });

  const copilotChat = useCopilotChat({
    id: `agent-${selectedRole}`,
  });
  const { visibleMessages } = copilotChat;

  const conversation = useConversation();
  
  const { 
    isRecording, 
    isProcessing: isVoiceProcessing, 
    startRecording, 
    stopRecording,
    speakText
  } = useVoiceCommands({
    onTranscription: (text) => {
      setQuery(text);
      if (text.trim().length > 5) {
        setTimeout(() => handleSendQuery(), 500);
      }
    },
    onError: (error) => {
      console.error("Voice command error:", error);
      toast.error("Voice recognition failed");
    }
  });

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useHotkeys('ctrl+k, cmd+k', (event) => {
    event.preventDefault();
    if (!isOpen) {
      onClose();
    }
  });

  const handleToggleRecording = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  };

  const sendMessage = async (input: string) => {
    if (!input.trim()) return;
    
    // Create a properly typed message object
    const newMessage: ExtendedMessage = {
      id: String(Date.now()),
      content: input,
      role: 'user'
    };

    copilotChat.appendMessage(newMessage);
    
    const result = await execute(
      `
      // Agent role: ${selectedRole}
      // Process this user query and return a helpful response
      const query = \`${input.replace(/`/g, '\\`')}\`;
      console.log("Processing query as ${selectedRole}:", query);
      
      // Return the response
      query;
      `,
      'javascript'
    );
    
    const responseText = result?.result || result?.stdout?.join("\n") || "I couldn't process your request.";
    setAiResponse(responseText);
    
    if (sessionId) {
      await recordInteraction(
        sessionId,
        selectedRole,
        `${selectedRole} Agent Query`,
        input,
        responseText,
        config.model || 'gpt-4o-mini',
        config.apiProvider || 'openai',
        {
          executionTime: Date.now(),
          inputTokens: input.length / 4, // Rough estimation
          outputTokens: responseText.length / 4, // Rough estimation
          agentType: selectedRole
        }
      );
    }
    
    if (isAudioEnabled) {
      try {
        const audioBase64 = await speakText(responseText);
        if (audioBase64) {
          setAudioResponse(audioBase64);
        }
      } catch (error) {
        console.error('Error generating speech:', error);
      }
    }
    
  };

  const handleSendQuery = async () => {
    if (!query.trim() || !user) return;
    
    setIsProcessing(true);
    
    try {
      sendMessage(query);
    } catch (error) {
      console.error('Error processing command:', error);
      toast.error('Failed to process your request');
    } finally {
      setIsProcessing(false);
    }
  };

  useCopilotAction({
    name: "switchAgentRole",
    description: "Switch to a different agent role (mentor, student, admin, expert)",
    parameters: [
      {
        name: "role",
        type: "string",
        description: "The role to switch to (mentor, student, admin, expert)",
        required: true,
      },
    ],
    handler: async ({ role }) => {
      const validRole = role as AgentRole;
      if (['mentor', 'student', 'admin', 'expert'].includes(validRole)) {
        setSelectedRole(validRole);
        toast.success(`Switched to ${validRole} role`);
      } else {
        toast.error(`Invalid role: ${role}`);
      }
    },
  });

  useCopilotAction({
    name: "navigateTo",
    description: "Navigate to a different page in the application",
    parameters: [
      {
        name: "path",
        type: "string",
        description: "The path to navigate to",
        required: true,
      },
    ],
    handler: async ({ path }) => {
      navigate(path);
      onClose();
      toast.success(`Navigated to ${path}`);
    },
  });

  const handleQueryChange = (value: string) => {
    setQuery(value);
    
    if (value.startsWith('/')) {
      const command = value.substring(1).toLowerCase();
      
      if (command === 'mentor') {
        setSelectedRole('mentor');
        setQuery('');
        toast.success('Switched to Mentor role');
      } else if (command === 'student') {
        setSelectedRole('student');
        setQuery('');
        toast.success('Switched to Student role');
      } else if (command === 'admin') {
        setSelectedRole('admin');
        setQuery('');
        toast.success('Switched to Admin role');
      } else if (command === 'expert') {
        setSelectedRole('expert');
        setQuery('');
        toast.success('Switched to Expert role');
      } else if (command === 'mute') {
        setIsAudioEnabled(false);
        setQuery('');
        toast.success('Audio responses disabled');
      } else if (command === 'unmute') {
        setIsAudioEnabled(true);
        setQuery('');
        toast.success('Audio responses enabled');
      } else if (command === 'help') {
        setAiResponse(`
          Available commands:
          - /mentor - Switch to Mentor role
          - /student - Switch to Student role
          - /admin - Switch to Admin role
          - /expert - Switch to Expert role
          - /mute - Disable audio responses
          - /unmute - Enable audio responses
          - /help - Show this help message
        `);
        setQuery('');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
      <div className="fixed left-[50%] top-[20%] z-50 grid w-full max-w-lg translate-x-[-50%] gap-4 border bg-background shadow-lg duration-200 sm:rounded-lg md:w-full">
        <Command className="rounded-lg border shadow-md">
          <div className="flex items-center border-b px-3">
            <div className="flex items-center gap-2 mr-2">
              {agentRoles.map((role) => (
                <Button
                  key={role.id}
                  variant={selectedRole === role.id ? "default" : "outline"}
                  size="sm"
                  className="h-8 gap-1"
                  onClick={() => setSelectedRole(role.id)}
                >
                  {role.icon}
                  <span className="hidden sm:inline">{role.name}</span>
                </Button>
              ))}
            </div>
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={`Ask ${selectedRole} agent...`}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendQuery();
                }
              }}
              disabled={isProcessing || isVoiceProcessing || isRecording}
            />
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleRecording}
                disabled={isProcessing}
                className={isRecording ? "bg-red-100 dark:bg-red-900 text-red-500" : ""}
              >
                {isRecording ? (
                  <MicOff className="h-4 w-4 text-red-500" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsAudioEnabled(!isAudioEnabled)}
              >
                {isAudioEnabled ? (
                  <Volume2 className="h-4 w-4" />
                ) : (
                  <VolumeX className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex flex-col p-4 max-h-[50vh] overflow-y-auto">
            {isProcessing || isVoiceProcessing ? (
              <div className="flex items-center justify-center p-4">
                <Loader className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">
                  {isVoiceProcessing ? "Processing voice..." : "Processing your request..."}
                </span>
              </div>
            ) : aiResponse ? (
              <div className="p-4 bg-muted/50 rounded-md whitespace-pre-line">
                {aiResponse}
              </div>
            ) : (
              <div className="text-center p-4 text-muted-foreground">
                <p className="mb-2">Try asking a question or use commands:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-muted rounded-md">/mentor - Switch role</div>
                  <div className="p-2 bg-muted rounded-md">/mute - Toggle audio</div>
                  <div className="p-2 bg-muted rounded-md">/help - Get help</div>
                  <div className="p-2 bg-muted rounded-md">Ctrl+K - Open/close</div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between p-2 border-t">
            <div className="text-xs text-muted-foreground">
              {selectedRole} agent {config.model || 'gpt-4o-mini'}
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleSendQuery}
              disabled={!query.trim() || isProcessing || isVoiceProcessing || isRecording}
              className="gap-1"
            >
              <Send className="h-3 w-3" />
              Send
            </Button>
          </div>
        </Command>
      </div>
    </div>
  );
};
